import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'

import { faCheckCircle } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import { useSetState } from '@domain-group/react-hooks'

import PageContext from '@contexts/pageContext'

import { generateUrls } from '@hooks/useApiLog'
import useApiLogs from '@hooks/useApiLogs'
import useWebhooks from '@hooks/useWebhooks'

import ListBlankState from '@components/ListBlankState'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import { apiLogEvents, apiTypes } from '@functions/apiLog'
import joinDateAndTimeStringsToISO from '@functions/joinDateAndTimeStringsToISO'

import type { WebhookModel } from '@models/types'

import ApiLogTable from '../ApiLogTable'

interface BuildPageFilterFieldsParams {
  providerId: null | number,
  providerWebhooks?: Partial<WebhookModel>[],
  setState: (state: object) => void,
}

const buildPageFilterFields = (params: BuildPageFilterFieldsParams) => {
  const {
    providerId, providerWebhooks, setState,
  } = params
  const style = { marginBottom: 16 }
  const boxProps = { marginBottom: 'large' }

  return [
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'portals',
      },
      key: 'entity_id',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'select',
        style,
      },
      key: 'api_type',
      label: 'API Type',
      values: apiTypes,
    },
    {
      callbacks: {
        onChange: (id: number) => setState({ providerId: id }),
        shouldRender: ({ api_type }: { api_type?: string }) => api_type === 'webhook',
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'providers',
        label: 'Webhook Owner',
        requestOptions: { include_provider_webhooks: true },
      },
      dependentKey: 'api_type',
      key: 'provider_id',
    },
    {
      callbacks: {
        shouldRender: ({ api_type }: { api_type?: string }) => (api_type === 'webhook' && !!providerId),
      },
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'select',
        style,
      },
      dependentKey: 'provider_id',
      key: 'webhook_id',
      label: 'Filter for Webhook',
      values: providerWebhooks?.map(webhook => ({
        key: webhook.id,
        label: `#${webhook.id} - ${webhook.title}`,
      })),
    },
    {
      callbacks: {
        shouldRender: ({ api_type }: { api_type?: string }) => !!api_type,
      },
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'select',
        style,
      },
      key: 'search_column',
      label: 'Filter Column for String',
      values: [
        { key: 'request_body', label: 'Request Body' },
        { key: 'response_body', label: 'Response Body' },
      ],
    },
    {
      callbacks: {
        shouldRender: ({ api_type }: { api_type?: string }) => !!api_type,
      },
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'String',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'select',
        style,
      },
      key: 'event',
      label: 'Event',
      values: apiLogEvents,
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'select',
        style,
      },
      key: 'success',
      label: 'Success',
      values: [
        { key: 'true', label: 'True' },
        { key: 'false', label: 'False' },
      ],
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        inputType: 'date',
        style,
      },
      key: 'start_date',
      label: 'Start Date',
    },
    {
      callbacks: {
        shouldRender: ({ start_date }: { start_date?: string }) => !!start_date,
      },
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        inputType: 'time',
        style,
      },
      key: 'start_time',
      label: 'Start Time',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        inputType: 'date',
        style,
      },
      key: 'end_date',
      label: 'End Date',
    },
    {
      callbacks: {
        shouldRender: ({ end_date }: { end_date?: string }) => !!end_date,
      },
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        inputType: 'time',
        style,
      },
      dependentKey: 'end_date',
      key: 'end_time',
      label: 'End Time',
    },
  ]
}

const defaultState = {
  providerId: undefined as undefined | number,
}

interface PageContentProps {
  pageFilters: {
    [key: string]: any,
  },
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props
  const {
    end_date,
    end_time,
    entity_id,
    start_date,
    start_time,
  } = pageFilters

  const [state, setState] = useSetState(defaultState)
  const { providerId } = state

  const endDate = joinDateAndTimeStringsToISO({
    date: end_date,
    time: end_time,
    options: { defaultToEndOfDay: true },
  })

  const startDate = joinDateAndTimeStringsToISO({ date: start_date, time: start_time })

  const apiLogsPayload = useApiLogs({
    filters: {
      ...pageFilters,
      entity_type: entity_id ? 'Portal' : undefined,
      end_date: endDate,
      start_date: startDate,
    },
    performHttpRequests: true,
    requestOptions: {
      include_api_actioned_by_user: true,
      include_api_log_webhook: !!providerId,
    },
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filteredApiLogs,
    filteredApiLogsCount,
    hasFilteredApiLogs,
    loading,
  } = apiLogsPayload

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const { apiLogsIndexUrl } = generateUrls()

  const webhooksPayload = useWebhooks({
    filters: {
      owner_id: providerId,
      owner_type: 'Provider',
    },
  })
  const { filteredWebhooks: providerWebhooks } = webhooksPayload

  const filterFieldParams = {
    providerId,
    providerWebhooks,
    setState,
  }

  return (
    <>
      <Helmet>
        <title>API Logs | Realhub</title>
      </Helmet>

      <PageHeader
        activeTabBarItemKey="apiLogs"
        tabBarItems={[
          {
            href: apiLogsIndexUrl,
            icon: faCheckCircle,
            key: 'apiLogs',
            title: 'API Logs',
          },
        ]}
        title="API Logs"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredApiLogs && <ListBlankState />}

            {!loading && hasFilteredApiLogs && (
              <DashboardModule title="API Logs">
                <Box flexDirection="column">
                  <Box flexDirection="column">
                    <ApiLogTable apiLogs={filteredApiLogs} />
                  </Box>

                  <DashboardModule.LoadMoreFooter
                    callbacks={{ loadMore }}
                    canLoadMore={canLoadMore}
                    entityCount={filteredApiLogsCount}
                    loading={loading}
                  />
                </Box>
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields(filterFieldParams)}
              pageFilters={pageFilters}
              title="Filters"
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
